<template>
  <div>
    <h1 class="page-header">{{ $t('holidayRule.pageTitle') }}</h1>
    <div>
      <ts-card>
        <ts-card-wrapper>
          <div class="ts-flex">
            <div>
              <ts-select v-model="model.subs_day1" :options="substituteDays" style="width: 200px;" />
              <i class="ts-gray-400"
                ><small>{{ $t('holidayRule.subsDay1') }}</small></i
              >
            </div>
            <div class="ts-px-2">
              <ts-select
                v-model="model.subs_day2"
                :placeholder="$t('holidayRule.subsDay2')"
                :options="substituteDays"
                class="ts-ml-2"
                style="width: 200px;"
              />
              <i class="ts-gray-400 ml-2"
                ><small>{{ $t('holidayRule.subsDay2') }}</small></i
              >
            </div>
            <div class="ts-flex-grow"></div>
            <ts-input v-model="search" :placeholder="$t('search')" style="width: 200px;" />
          </div>
        </ts-card-wrapper>
        <div id="container-table">
          <ts-table :records="model.holiday_detail">
            <template v-slot:thead>
              <thead>
                <tr>
                  <th v-for="(column, key) in columns" :key="key">{{ column.name }}</th>
                  <th style="text-align:center; width: 1px;">
                    <a href="#" v-tooltip="$t('tooltip.addItem')" class="text-primary" @click.prevent="addNewRow">
                      <i class="fas fa-plus"></i>
                    </a>
                  </th>
                </tr>
                <TableProgressBar v-show="loading" :columnLength="columns.length + 1" />
              </thead>
            </template>
            <template v-slot="{ record, index }">
              <td>
                <input
                  type="text"
                  :value="record.holiday_name_kh"
                  @input="e => (record.holiday_name_kh = e.target.value)"
                  class="form-control only-border-bottom"
                />
                <div class="invalid-feedback" v-if="errors.has('holiday_detail.' + index + '.holiday_name_kh')">
                  {{ errors.first('holiday_detail.' + index + '.holiday_name_kh') }}
                </div>
              </td>
              <td>
                <input
                  type="text"
                  :value="record.holiday_name_en"
                  @input="e => (record.holiday_name_en = e.target.value)"
                  class="form-control only-border-bottom"
                />
                <div class="invalid-feedback" v-if="errors.has('holiday_detail.' + index + '.holiday_name_en')">
                  {{ errors.first('holiday_detail.' + index + '.holiday_name_en') }}
                </div>
              </td>
              <td style="width: 200px">
                <ts-select
                  :value="record.holiday_month"
                  @input="value => (record.holiday_month = value)"
                  :options="months"
                  class="form-control only-border-bottom"
                />
                <div class="invalid-feedback" v-if="errors.has('holiday_detail.' + index + '.holiday_month')">
                  {{ errors.first('holiday_detail.' + index + '.holiday_month') }}
                </div>
              </td>
              <td style="width: 150px">
                <ts-select
                  :value="record.holiday_day"
                  @input="value => (record.holiday_day = value)"
                  :options="days(record.holiday_month)"
                  class="form-control only-border-bottom"
                />
                <div class="invalid-feedback" v-if="errors.has('holiday_detail.' + index + '.holiday_day')">
                  {{ errors.first('holiday_detail.' + index + '.holiday_day') }}
                </div>
              </td>
              <td class="ts-align-middle">
                <a href="#" v-tooltip="$t('tooltip.delete')" class="text-danger" @click.prevent="destroy(record)">
                  <i class="fas fa-trash"></i>
                </a>
              </td>
            </template>
          </ts-table>
        </div>
        <ts-card-wrapper>
          <div class="row ts-mt-6">
            <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
              <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">{{
                $t('button.save')
              }}</ts-button>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>
    </div>
  </div>
</template>

<script>
import moment, { months } from 'moment'
import { Errors } from 'form-backend-validation'
import { cloneDeep, range } from 'lodash'
import { mapState } from 'vuex'
import TableProgressBar from '@/components/common/TsTable/TableProgressBar'

export default {
  name: 'holiday-rule',
  components: { TableProgressBar },
  i18n: {
    messages: {
      en: {
        dayOfWeek: {
          Monday: 'Monday',
          Tuesday: 'Tuesday',
          Wednesday: 'Wednesday',
          Thursday: 'Thursday',
          Friday: 'Friday',
          Saturday: 'Saturday',
          Sunday: 'Sunday'
        }
      },
      kh: {
        dayOfWeek: {
          Monday: 'ច័ន្ទ',
          Tuesday: 'អង្គារ',
          Wednesday: 'ពុធ',
          Thursday: 'ព្រហស្បតិ៌',
          Friday: 'សុក្រ',
          Saturday: 'សៅរ៌',
          Sunday: 'អាទិត្យ'
        }
      }
    }
  },
  data() {
    return {
      model: {
        subs_day1: '',
        subs_day2: '',
        holiday_detail: []
      },
      errors: new Errors(),
      saving: false,
      loading: false
    }
  },
  computed: {
    ...mapState('hr/holidayRule', { holidayRules: 'resources', pagination: 'pagination', substitute: 'substitute' }),
    search: {
      get() {
        return this.$store.state.hr.holidayRule.search
      },
      set(newValue) {
        this.$store.commit('hr/holidayRule/SET_SEARCH', newValue)
        this.$store.commit('hr/holidayRule/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('holidayRule.holidayNameKh'),
          sortKey: 'holiday_detail.holiday_name_kh'
        },
        {
          name: this.$t('holidayRule.holidayNameEn'),
          sortKey: 'holiday_detail.holiday_name_en'
        },
        {
          name: this.$t('holidayRule.holidayMonth'),
          sortKey: 'holiday_detail.holiday_month'
        },
        {
          name: this.$t('holidayRule.holidayDay'),
          sortKey: 'holiday_detail.holiday_day'
        }
      ]
    },
    substituteDays() {
      return [
        { text: this.$t('dayOfWeek.Monday'), value: 'Monday' },
        { text: this.$t('dayOfWeek.Tuesday'), value: 'Tuesday' },
        { text: this.$t('dayOfWeek.Wednesday'), value: 'Wednesday' },
        { text: this.$t('dayOfWeek.Thursday'), value: 'Thursday' },
        { text: this.$t('dayOfWeek.Friday'), value: 'Friday' },
        { text: this.$t('dayOfWeek.Saturday'), value: 'Saturday' },
        { text: this.$t('dayOfWeek.Sunday'), value: 'Sunday' }
      ]
    },
    months() {
      return moment.months().map((month, index) => {
        return {
          text: month,
          value: index + 1
        }
      })
    },
    days: vm => month => {
      if (month === '') return []

      const startOfMonth = moment()
        .month(month - 1)
        .startOf('month')

      const endOfMonth = moment()
        .month(month - 1)
        .endOf('month')

      return range(1, endOfMonth.diff(startOfMonth, 'days') + 2)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData(attributes) {
      this.loading = true

      Promise.all([
        this.$store.dispatch('hr/holidayRule/fetchSubstitute'),
        this.$store.dispatch('hr/holidayRule/fetch', attributes)
      ])
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    save() {
      this.errors = new Errors()
      this.saving = true

      return this.$store
        .dispatch('hr/holidayRule/create', this.model)
        .then(response => {
          return this.$store.dispatch('hr/holidayRule/fetch').then(res => {
            this.$toasted.success(response.message)

            return Promise.resolve(res)
          })
        })
        .catch(error => {
          if (error.errors) this.errors = new Errors(error.errors)
          else this.$toasted.error(error.message)

          return Promise.reject(error)
        })
        .finally(() => (this.saving = false))
    },
    destroy(record) {
      this.model.holiday_detail.splice(this.model.holiday_detail.indexOf(record), 1)
    },
    addNewRow() {
      this.model.holiday_detail.push({
        holiday_name_kh: '',
        holiday_name_en: '',
        holiday_day: '',
        holiday_month: ''
      })
    }
  },
  watch: {
    substitute: function(substitute) {
      this.model.subs_day1 = substitute.subs_day1
      this.model.subs_day2 = substitute.subs_day2
    },
    holidayRules: function(holidayRules) {
      this.model.holiday_detail = cloneDeep(holidayRules)
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('hr/holidayRule/RESET_STATE')
    next()
  }
}
</script>

<style lang="css" scoped>
#container-table {
	max-height: 67vh;
	overflow-y:scroll;
	overflow-x: hidden;
	scrollbar-width: none;
   -ms-overflow-style: none;
}
::-webkit-scrollbar {
		width: 3px;  /* Remove scrollbar space */
		background: transparent;  /* Optional: just make scrollbar invisible */
}
/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
		background: rgb(194, 194, 194);
}
.table > thead > tr > th {
	  position: sticky;
		position: -webkit-sticky;
		top: 0;
		z-index: 999;
}
.table > tbody > tr > td{
	padding-top: 5px !important;
	padding-bottom: 5px !important;
	border: none !important
}
.only-border-bottom {
	border-top: none;
	border-left: none;
	border-right: none;
}
</style>
